import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useBandDisagreements } from "./hooks/usePerformanceData";
import { useBandDisagreementsState } from "./band-disagreements/hooks/useBandDisagreementsState";
import { useBandDisagreementActions } from "./band-disagreements/hooks/useBandDisagreementActions";
import { useUnitBandDisagreements } from "./band-disagreements/hooks/useUnitBandDisagreements";
import BandDisagreementRecords from "./band-disagreements/BandDisagreementRecords";
import BandDisagreementDiscussion from "./band-disagreements/BandDisagreementDiscussion";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

const BandDisagreementsTab = () => {
  const [supervisorName, setSupervisorName] = useState<string>("");
  const { activeYear } = usePerformanceYear();
  
  const {
    isSupervisor,
    isUnitHead,
    isLoading,
    activeTab,
    setActiveTab,
    selectedRecord,
    setSelectedRecord,
    isRecordsExpanded,
    setIsRecordsExpanded,
    resolutionOutcome,
    setResolutionOutcome,
    resolutionNotes,
    setResolutionNotes,
    currentUserId
  } = useBandDisagreementsState();
  
  const {
    data: disagreements = [],
    isLoading: isLoadingDisagreements,
    error,
    refetch
  } = useBandDisagreements();

  const {
    data: unitDisagreements = [],
    isLoading: isLoadingUnitDisagreements,
    error: unitError,
    refetch: refetchUnit
  } = useUnitBandDisagreements();

  const { handleSaveAndClose } = useBandDisagreementActions(refetch, activeYear);

  // Fetch supervisor name
  useEffect(() => {
    const fetchSupervisorName = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error("Error getting user:", userError);
          return;
        }

        // Get current user's record to find supervisor_id
        const { data: userData, error: userDataError } = await supabase
          .from('users')
          .select('supervisor_id')
          .eq('id', user.id)
          .single();

        if (userDataError || !userData?.supervisor_id) {
          console.log("No supervisor found or error:", userDataError);
          return;
        }

        // Get supervisor's full_name using supervisor_id
        const { data: supervisorData, error: supervisorError } = await supabase
          .from('users')
          .select('full_name')
          .eq('id', userData.supervisor_id)
          .single();

        if (supervisorError) {
          console.error("Error fetching supervisor data:", supervisorError);
          return;
        }

        if (supervisorData?.full_name) {
          setSupervisorName(supervisorData.full_name);
        }
      } catch (error) {
        console.error("Error fetching supervisor name:", error);
      }
    };

    fetchSupervisorName();
  }, []);

  const handleActionClick = (record: any) => {
    setSelectedRecord(selectedRecord?.id === record.id ? null : record);
  };

  const onSaveAndClose = () => {
    handleSaveAndClose(
      selectedRecord,
      isSupervisor,
      resolutionOutcome,
      resolutionNotes,
      setSelectedRecord,
      isUnitHead
    );
  };

  // Filter disagreements for Personal Goals (user's own) vs Team Members
  const userOwnDisagreements = disagreements.filter(d => d.user_id === currentUserId);
  
  // For Manage Team Members tab, only show direct reports (not indirect unit members)
  const teamMemberDisagreements = disagreements.filter(d => 
    d.user_id !== currentUserId && d.supervisor_id === currentUserId
  );

  const renderBandDisagreementRecords = (disagreementsToShow: any[], showActions = true, treatAsTeamMember = false) => (
    <div className="space-y-2">
      <BandDisagreementRecords
        disagreementsToShow={disagreementsToShow}
        showActions={showActions}
        isRecordsExpanded={isRecordsExpanded}
        setIsRecordsExpanded={setIsRecordsExpanded}
        isLoadingDisagreements={isLoadingDisagreements || isLoadingUnitDisagreements}
        error={error || unitError}
        onActionClick={handleActionClick}
        selectedRecord={selectedRecord}
        isSupervisor={treatAsTeamMember ? false : (isSupervisor || isUnitHead)}
      />

      {selectedRecord && (
        <BandDisagreementDiscussion
          selectedRecord={selectedRecord}
          isSupervisor={treatAsTeamMember ? false : (isSupervisor || isUnitHead)}
          resolutionOutcome={resolutionOutcome}
          setResolutionOutcome={setResolutionOutcome}
          resolutionNotes={resolutionNotes}
          setResolutionNotes={setResolutionNotes}
          onClose={() => setSelectedRecord(null)}
          onSaveAndClose={onSaveAndClose}
        />
      )}
    </div>
  );

  // Custom handler for unit tab with proper refetch
  const handleUnitSaveAndClose = () => {
    handleSaveAndClose(
      selectedRecord,
      isSupervisor,
      resolutionOutcome,
      resolutionNotes,
      (record) => {
        setSelectedRecord(record);
        // Refetch unit disagreements to get updated values
        refetchUnit();
      },
      isUnitHead
    );
  };

  const renderUnitBandDisagreementRecords = (disagreementsToShow: any[], showActions = true) => (
    <div className="space-y-2">
      <BandDisagreementRecords
        disagreementsToShow={disagreementsToShow}
        showActions={showActions}
        isRecordsExpanded={isRecordsExpanded}
        setIsRecordsExpanded={setIsRecordsExpanded}
        isLoadingDisagreements={isLoadingUnitDisagreements}
        error={unitError}
        onActionClick={handleActionClick}
        selectedRecord={selectedRecord}
        isSupervisor={isUnitHead}
      />

      {selectedRecord && (
        <BandDisagreementDiscussion
          selectedRecord={selectedRecord}
          isSupervisor={isUnitHead}
          resolutionOutcome={resolutionOutcome}
          setResolutionOutcome={setResolutionOutcome}
          resolutionNotes={resolutionNotes}
          setResolutionNotes={setResolutionNotes}
          onClose={() => setSelectedRecord(null)}
          onSaveAndClose={handleUnitSaveAndClose}
        />
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading band disagreements...</div>
      </div>
    );
  }

  if (!isSupervisor && !isUnitHead) {
    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Band Disagreements</h2>
          {supervisorName && (
            <div className="flex items-center gap-2 text-white text-[11px]">
              <span>Supervisor:</span>
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {supervisorName}
              </Badge>
            </div>
          )}
        </div>
        
        {userOwnDisagreements.length > 0 && renderBandDisagreementRecords(userOwnDisagreements, true)}
        
        {userOwnDisagreements.length === 0 && !isLoadingDisagreements && !error && (
          <Card className="bg-secondary/50 border border-primary">
            <CardContent className="border border-gray-500/30 rounded-lg py-4">
              <div className="text-center">
                <p className="text-white text-sm">You have not raised any band disagreement.</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Band Disagreements</h2>
        {supervisorName && (
          <div className="flex items-center gap-2 text-white text-[11px]">
            <span>Supervisor:</span>
            <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              {supervisorName}
            </Badge>
          </div>
        )}
      </div>
      
      <div className="bg-secondary/30 rounded-lg">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex border-b border-primary/20">
            <TabsList className="bg-transparent">
              <TabsTrigger
                value="personal-goals"
                className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
              >
                Personal Goals
              </TabsTrigger>
              {(isSupervisor || isUnitHead) && (
                <TabsTrigger
                  value="manage-team-members"
                  className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
                >
                  Manage Team Members
                </TabsTrigger>
              )}
              {isUnitHead && (
                <TabsTrigger
                  value="manage-unit"
                  className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
                >
                  Manage Unit
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          
          <TabsContent value="personal-goals" className="mt-4">
            <div className="space-y-6">
              {userOwnDisagreements.length > 0 ? renderBandDisagreementRecords(userOwnDisagreements, true, true) : (
                <Card className="border border-gray-500/30 rounded-lg">
                  <CardContent className="py-4">
                    <div className="text-center">
                      <p className="text-white text-sm">You have not raised any band disagreement.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          
          {(isSupervisor || isUnitHead) && (
            <TabsContent value="manage-team-members" className="mt-4">
              <div className="space-y-6">
                {teamMemberDisagreements.length > 0 && renderBandDisagreementRecords(teamMemberDisagreements, true)}
                
                {teamMemberDisagreements.length === 0 && !isLoadingDisagreements && !error && (
                  <Card className="border border-gray-500/30 rounded-lg">
                    <CardContent className="py-4">
                      <div className="text-center">
                        <p className="text-white text-sm">No band disagreements found for your team members.</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {isUnitHead && (
            <TabsContent value="manage-unit" className="mt-4">
              <div className="space-y-6">
                {unitDisagreements.length > 0 && renderUnitBandDisagreementRecords(unitDisagreements, true)}
                
                {unitDisagreements.length === 0 && !isLoadingUnitDisagreements && !unitError && (
                  <Card className="border border-gray-500/30 rounded-lg">
                    <CardContent className="py-4">
                      <div className="text-center">
                        <p className="text-white text-sm">No band disagreements found for your unit.</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default BandDisagreementsTab;
